import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Loader2, KeyRound, LockOpen, MessageSquare, Megaphone } from "lucide-react";
import { MessageDialog } from "@/admin/MessageDialog";

const COUNTRIES = [
  { country: "United Arab Emirates", timezone: "Asia/Dubai", currency: "AED" },
  { country: "India", timezone: "Asia/Kolkata", currency: "INR" },
  { country: "United Kingdom", timezone: "Europe/London", currency: "GBP" },
  { country: "United States", timezone: "America/New_York", currency: "USD" },
];
const TIERS = [
  { name: "Starter", limit: 2000 },
  { name: "Bronze", limit: 5000 },
  { name: "Silver", limit: 10000 },
  { name: "Gold", limit: 18000 },
  { name: "Platinum", limit: 30000 },
];

const blank = {
  name: "", email: "", password: "", country: "United Arab Emirates",
  timezone: "Asia/Dubai", currency: "AED", language: "en",
  deposit_tier: "Starter", credit_limit: 2000, outstanding_credit: 0,
  agent_code: "", status: "active",
};

export default function Agents() {
  const { user } = useAuth();
  const [agents, setAgents] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tempCreds, setTempCreds] = useState(null);
  const [msgTarget, setMsgTarget] = useState(null);
  const resetPassword = async (a) => {
    if (!window.confirm(`Issue a new password for ${a.name}? The old password stops working immediately.`)) return;
    try {
      const { data } = await api.post(`/admin/agents/${a.id}/reset-password`, {});
      setTempCreds({ email: data.email, password: data.temp_password });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const unlock = async (a) => {
    try {
      await api.post(`/admin/agents/${a.id}/unlock`);
      toast.success(`${a.name} unlocked`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const load = () => api.get("/admin/agents").then((r) => setAgents(r.data));
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(blank);
    setEditId(null);
    setOpen(true);
  };
  const openEdit = (a) => {
    setForm({ ...blank, ...a, password: "" });
    setEditId(a.id);
    setOpen(true);
  };

  const setCountry = (c) => {
    const meta = COUNTRIES.find((x) => x.country === c);
    setForm((f) => ({ ...f, country: c, timezone: meta.timezone, currency: meta.currency }));
  };
  const setTier = (t) => {
    const meta = TIERS.find((x) => x.name === t);
    setForm((f) => ({ ...f, deposit_tier: t, credit_limit: meta.limit }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, credit_limit: Number(form.credit_limit), outstanding_credit: Number(form.outstanding_credit) };
      if (editId) {
        await api.put(`/admin/agents/${editId}`, payload);
        toast.success("Agent updated");
      } else {
        const { data } = await api.post("/admin/agents", payload);
        if (data.temp_password) {
          setTempCreds({ email: data.email, password: data.temp_password });
        }
        toast.success("Agent created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Agents"
        subtitle="Create and manage field sales agents, their locale and credit tier."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setMsgTarget({ agent: null })} data-testid="message-all-button"><Megaphone className="w-4 h-4 mr-1" /> Message all</Button>
            {user?.role === "admin" && <Button onClick={openNew} data-testid="add-agent-button">
              <Plus className="w-4 h-4 mr-1" /> New agent
            </Button>}
          </div>
        }
      />

      {!agents ? (
        <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : agents.length === 0 ? (
        <Empty>No agents yet. Create your first agent.</Empty>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-medium px-4 py-3">Agent</th>
                  <th className="text-left font-medium px-4 py-3">Locale</th>
                  <th className="text-left font-medium px-4 py-3">Tier</th>
                  <th className="text-right font-medium px-4 py-3">Credit used / limit</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {agents.map((a) => (
                  <tr key={a.id} className="border-b border-border/50 hover:bg-secondary/40" data-testid={`agent-row-${a.agent_code}`}>
                    <td className="px-4 py-3">
                      <Link
                        to={`/dashboard/agents/${a.id}`}
                        className="font-medium text-primary hover:underline"
                        data-testid={`agent-name-link-${a.agent_code}`}
                      >
                        {a.name}
                      </Link>
                      <div className="text-xs text-muted-foreground">{a.email} · {a.agent_code}</div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {a.country}<br />
                      <span className="text-xs">{a.currency} · {a.timezone}</span>
                    </td>
                    <td className="px-4 py-3"><Badge variant="secondary">{a.deposit_tier}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      {money(a.outstanding_credit, "AED")} <span className="text-muted-foreground">/ {money(a.credit_limit, "AED")}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={a.status === "active" ? "default" : a.status === "exiting" ? "destructive" : "outline"}>{a.status}</Badge>
                      {a.locked && <Badge variant="destructive" className="ml-1" data-testid={`locked-badge-${a.agent_code}`}>locked</Badge>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" title="Message" onClick={() => setMsgTarget({ agent: a })} data-testid={`message-agent-${a.agent_code}`}><MessageSquare className="w-4 h-4" /></Button>
                      {user?.role === "admin" && a.locked && <Button variant="ghost" size="sm" title="Unlock login" onClick={() => unlock(a)} data-testid={`unlock-agent-${a.agent_code}`}><LockOpen className="w-4 h-4" /></Button>}
                      {user?.role === "admin" && <Button variant="ghost" size="sm" title="Issue new password" onClick={() => resetPassword(a)} data-testid={`reset-password-${a.agent_code}`}><KeyRound className="w-4 h-4" /></Button>}
                      <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`edit-agent-${a.agent_code}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit agent" : "New agent"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Full name" full>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agent-name-input" />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} disabled={!!editId} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="agent-email-input" />
            </Field>
            <Field label="WhatsApp / phone (with country code)">
              <Input value={form.phone || ""} placeholder="+9715xxxxxxx" onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="agent-phone-input" />
            </Field>
            <Field label="Agent code">
              <Input value={form.agent_code} onChange={(e) => setForm({ ...form, agent_code: e.target.value })} data-testid="agent-code-input" />
            </Field>
            {user?.role === "admin" && (
              <Field label="Manager e-mail (team scoping, optional)" full>
                <Input type="email" value={form.manager_email || ""} placeholder="manager@orgaviya.com" onChange={(e) => setForm({ ...form, manager_email: e.target.value || null })} data-testid="agent-manager-email-input" />
              </Field>
            )}
            <Field label={editId ? "Reset password (optional)" : "Temporary password (blank = auto-generated, shown once)"} full>
              <Input type="text" value={form.password} placeholder={editId ? "Leave blank to keep current" : "Leave blank to auto-generate"} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="agent-password-input" />
            </Field>
            <Field label="Country">
              <Select value={form.country} onValueChange={setCountry}>
                <SelectTrigger data-testid="agent-country-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => <SelectItem key={c.country} value={c.country}>{c.country}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Currency / TZ">
              <Input value={`${form.currency} · ${form.timezone}`} disabled />
            </Field>
            <Field label="Deposit tier">
              <Select value={form.deposit_tier} onValueChange={setTier}>
                <SelectTrigger data-testid="agent-tier-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIERS.map((t) => <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Credit limit (AED)">
              <Input type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} data-testid="agent-credit-limit-input" />
            </Field>
            <Field label="Credit used (auto: unsold stock + unpaid invoices)">
              <Input type="number" value={form.outstanding_credit} disabled data-testid="agent-outstanding-readonly" />
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} data-testid="save-agent-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editId ? "Save changes" : "Create agent"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MessageDialog target={msgTarget} onClose={() => setMsgTarget(null)} />

      <Dialog open={!!tempCreds} onOpenChange={(v) => !v && setTempCreds(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Agent created — share these credentials</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This password is shown only once. Share it securely with the agent — agents cannot change it themselves;
            they will be required to set a new password on first login.
          </p>
          {tempCreds && (
            <div className="mt-2 rounded-lg border border-border bg-secondary/40 p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="font-medium" data-testid="temp-cred-email">{tempCreds.email}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Temp password</span><span className="font-mono font-semibold" data-testid="temp-cred-password">{tempCreds.password}</span></div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard?.writeText(`${tempCreds.email} / ${tempCreds.password}`);
                toast.success("Copied");
              }}
              data-testid="copy-temp-cred-button"
            >
              Copy
            </Button>
            <Button onClick={() => setTempCreds(null)} data-testid="close-temp-cred-button">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
